import * as React from "react";
import pluginRegistry, {ActionEditProps} from "../pluginRegistry";
import {BaseComponent} from "components/BaseComponent/BaseComponent";
import SpecialVariables from "client/specialVariables";
import {repository} from "clientInstance";
import Roles from "../Roles";
import {ActionSummaryProps} from "../actionSummaryProps";
import {ActionExecutionLocation} from "../../../client/resources/actionExecutionLocation";
import FeedResource, {BuiltInFeedId} from "client/resources/feedResource";
import PackageSelector from "components/PackageSelector/PackageSelector";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";
import CommonSummaryHelper from "utils/CommonSummaryHelper/CommonSummaryHelper";
import ExpandableFormSection, {SummaryNode} from "components/form/Sections/ExpandableFormSection";
import FormSectionHeading from "components/form/Sections/FormSectionHeading";
import AzurePowerShellScriptInfo from "components/Actions/azurePowerShell/AzurePowerShellScriptInfo";
import AzureServerTargetRolesInfo from "components/Actions/azurePowerShell/AzureServerTargetRolesInfo";
import WarningFormSection from "components/form/Sections/WarningFormSection";
import AzureServiceFabricSdkRequiredInfo from "components/Actions/azureServiceFabricApp/AzureServiceFabricSdkRequiredInfo";
import { VariableLookupText } from "components/form/Text/Text";
import AzureServiceFabricAccountSelector, {
    AADCredentialType,
    AzureServiceFabricAccountSelectorProperties,
    SecurityMode
} from "components/Actions/azureServiceFabricApp/AzureServiceFabricAccountSelector";
import Note from "components/form/Note/Note";
import Summary from "components/form/Sections/Summary";
import { BoundStringCheckbox } from "components/form/Checkbox/StringCheckbox";
import { BoundStringRadioButtonGroup } from "components/form/RadioButton/RadioButtonGroup";
import RadioButton from "components/form/RadioButton/RadioButton";
import {getFeedName} from "../getFeedName";
import { TargetRoles } from "areas/projects/components/DeploymentProcess/ActionDetails";

interface AzureServiceFabricAppActionSummaryState {
    feedName: string;
}

class AzureServiceFabricAppActionSummary extends BaseComponent<ActionSummaryProps, AzureServiceFabricAppActionSummaryState> {
    constructor(props: ActionSummaryProps) {
        super(props);
        this.state = {feedName: null };
    }

    async componentDidMount() {
        this.setState({feedName: await getFeedName(this.getFeedId())});
    }

    render() {
        return <div>
            Deploy Azure Service Fabric Application
            <strong> {this.props.properties[SpecialVariables.Action.Package.PackageId]} </strong>
            from {this.state.feedName ? <strong>{this.state.feedName}</strong> : <em>{this.getFeedId()}</em>}
            {this.props.targetRolesAsCSV && <span> on behalf of targets in <Roles rolesAsCSV={this.props.targetRolesAsCSV} /> </span>}
        </div>;
    }

    private getFeedId() {
        return this.props.properties[SpecialVariables.Action.Package.FeedId] as string;
    }
}

enum UpgradeBehavior {
    None = "None",
    ForceUpgrade = "ForceUpgrade",
    VetoUpgrade = "VetoUpgrade"
}

enum OverwriteBehavior {
    Never = "Never",
    Always = "Always",
    SameAppTypeAndVersion = "SameAppTypeAndVersion"
}

interface AzureServiceFabricAppProperties extends AzureServiceFabricAccountSelectorProperties {
    "Octopus.Action.Package.FeedId": string;
    "Octopus.Action.Package.PackageId": string;
    "Octopus.Action.ServiceFabric.PublishProfileFile": string;
    "Octopus.Action.ServiceFabric.DeployOnly": string;
    "Octopus.Action.ServiceFabric.UnregisterUnusedApplicationVersionsAfterUpgrade": string;
    "Octopus.Action.ServiceFabric.OverrideUpgradeBehavior": string;
    "Octopus.Action.ServiceFabric.OverwriteBehavior": string;
    "Octopus.Action.ServiceFabric.SkipPackageValidation": string;
    "Octopus.Action.ServiceFabric.CopyPackageTimeoutSec": string;
}

interface AzureServiceFabricAppState {
    feeds: FeedResource[];
    resetValues: {
        ClientCertVariable: string;
    };
}

class AzureServiceFabricAppActionEdit extends BaseComponent<ActionEditProps<AzureServiceFabricAppProperties>, AzureServiceFabricAppState> {
    constructor(props: any) {
        super(props);

        this.state = {
            feeds: [],
            resetValues: {
                ClientCertVariable: props.properties["Octopus.Action.ServiceFabric.ClientCertVariable"]
            }
        };
    }

    componentDidMount() {
        this.props.doBusyTask(async () => {
            this.setState({
                feeds: await repository.Feeds.all(),
            });
            const properties: any = {};

            if (!this.props.properties["Octopus.Action.ServiceFabric.OverrideUpgradeBehavior"]) {
                properties["Octopus.Action.ServiceFabric.OverrideUpgradeBehavior"] = UpgradeBehavior.None;
            }
            if (!this.props.properties["Octopus.Action.ServiceFabric.OverwriteBehavior"]) {
                properties["Octopus.Action.ServiceFabric.OverwriteBehavior"] = OverwriteBehavior.SameAppTypeAndVersion;
            }
            if (!this.props.properties["Octopus.Action.ServiceFabric.SecurityMode"]) {
                properties["Octopus.Action.ServiceFabric.SecurityMode"] = SecurityMode.Unsecure;
            }
            if (!this.props.properties["Octopus.Action.ServiceFabric.AadCredentialType"]) {
                properties["Octopus.Action.ServiceFabric.AadCredentialType"] = AADCredentialType.UserCredential;
            }
            if (!this.props.properties["Octopus.Action.Package.FeedId"]) {
                properties["Octopus.Action.Package.FeedId"] = BuiltInFeedId;
            }
            this.props.setProperties(properties, true);
        });
    }

    render() {
        const properties = this.props.properties;

        return <div>
            <WarningFormSection>
                <AzureServiceFabricSdkRequiredInfo />
                <AzurePowerShellScriptInfo actionType={this.props.plugin.actionType}/>
            </WarningFormSection>

            <FormSectionHeading title="Package"/>

            <ExpandableFormSection
                errorKey="Octopus.Action.Package.PackageId|Octopus.Action.Package.FeedId"
                title="Package"
                summary={CommonSummaryHelper.packageSummary(properties, this.state.feeds)}
                help={<span>This step is used to deploy the contents of a Service Fabric package. The package that you wish to deploy should contain
                    your <em>ApplicationParameters</em> and <em>PublishProfiles</em> folders, as well as your application package folder(s). Please
                    see the <ExternalLink href="ServiceFabricPackaging">Service Fabric Packaging guide</ExternalLink> to learn
                    about what your package should contain, and how to create them.</span>}>
                <PackageSelector
                    setProperties={this.props.setProperties}
                    properties={this.props.properties as any}
                    projectId={this.props.projectId}
                    feeds={this.state.feeds}
                    getFieldError={this.props.getFieldError}
                    localNames={this.props.localNames}
                    errors={this.props.errors} />
            </ExpandableFormSection>

            <FormSectionHeading title="Service Fabric Cluster"/>

            <AzureServiceFabricAccountSelector
                projectId={this.props.projectId}
                localNames={this.props.localNames}
                properties={this.props.properties}
                clientCertVariableResetValue={this.state.resetValues.ClientCertVariable}
                setProperties={this.props.setProperties}
                getFieldError={this.props.getFieldError}
                doBusyTask={this.props.doBusyTask}/>

            <FormSectionHeading title="Deployment"/>

            <ExpandableFormSection
                errorKey="Octopus.Action.ServiceFabric.PublishProfileFile"
                title="Publish Profile File"
                summary={properties["Octopus.Action.ServiceFabric.PublishProfileFile"]
                    ? Summary.summary(properties["Octopus.Action.ServiceFabric.PublishProfileFile"])
                    : Summary.placeholder("Publish profile file not provided")}
                help={"Select the path to your publish profile file."}>
                <VariableLookupText
                    localNames={this.props.localNames}
                    projectId={this.props.projectId}
                    value={properties["Octopus.Action.ServiceFabric.PublishProfileFile"]}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.ServiceFabric.PublishProfileFile"]: x})}
                    error={this.props.getFieldError("Octopus.Action.ServiceFabric.PublishProfileFile")}
                    hintText="eg. PublishProfiles\\Cloud.xml"
                    label="Publish profile file" />
                <Note>Octopus will use the ApplicationParameters file referenced in this publish profile. This value is typically <code>PublishProfiles\Cloud.xml</code></Note>
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Octopus.Action.ServiceFabric.DeployOnly"
                title="Deploy Only"
                summary={properties["Octopus.Action.ServiceFabric.DeployOnly"] && properties["Octopus.Action.ServiceFabric.DeployOnly"] !== "False"
                    ? Summary.summary(<span>The application will <strong>not</strong> but created or updated</span>)
                    : Summary.default("The application will be created or updated")}
                help="If selected, the Service Fabric application will not be created or upgraded after registering the application type.">
                <BoundStringCheckbox
                    variableLookup={{
                        localNames: this.props.localNames,
                        projectId: this.props.projectId
                    }}
                    resetValue={"False"}
                    value={properties["Octopus.Action.ServiceFabric.DeployOnly"]}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.ServiceFabric.DeployOnly"]: x})}
                    label="Deploy only" />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Octopus.Action.ServiceFabric.UnregisterUnusedApplicationVersionsAfterUpgrade"
                title="Unregister Unused Versions"
                summary={properties["Octopus.Action.ServiceFabric.UnregisterUnusedApplicationVersionsAfterUpgrade"]
                && properties["Octopus.Action.ServiceFabric.UnregisterUnusedApplicationVersionsAfterUpgrade"] !== "False"
                    ? Summary.summary(<span>Unused versions <strong>will</strong> be unregistered</span>)
                    : Summary.default("Unused versions will not be unregistered")}
                help="Select to unregister any unused application versions that exist after an upgrade is finished.">
                <BoundStringCheckbox
                    variableLookup={{
                        localNames: this.props.localNames,
                        projectId: this.props.projectId
                    }}
                    resetValue={"False"}
                    value={properties["Octopus.Action.ServiceFabric.UnregisterUnusedApplicationVersionsAfterUpgrade"]}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.ServiceFabric.UnregisterUnusedApplicationVersionsAfterUpgrade"]: x})}
                    label="Unregister" />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Octopus.Action.ServiceFabric.OverrideUpgradeBehavior"
                title="Override Upgrade Behavior"
                summary={this.upgradeBehaviorSummary()}
                help="Indicates the behavior used to override the upgrade settings specified by the publish profile.">
                <BoundStringRadioButtonGroup
                    variableLookup={{
                        localNames: this.props.localNames,
                        projectId: this.props.projectId
                    }}
                    resetValue={UpgradeBehavior.None}
                    value={properties["Octopus.Action.ServiceFabric.OverrideUpgradeBehavior"]}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.ServiceFabric.OverrideUpgradeBehavior"]: x})}
                    label="Upgrade behavior">
                    <RadioButton value={UpgradeBehavior.None} label="None" isDefault/>
                    <RadioButton value={UpgradeBehavior.ForceUpgrade} label="Force upgrade"/>
                    <RadioButton value={UpgradeBehavior.VetoUpgrade} label="Veto upgrade"/>
                </BoundStringRadioButtonGroup>
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Octopus.Action.ServiceFabric.OverwriteBehavior"
                title="Overwrite Behavior"
                summary={this.overwriteBehaviorSummary()}
                help="Overwrite behavior if an application exists in the cluster with the same name. This setting is not applicable when upgrading an application.">
                <BoundStringRadioButtonGroup
                    variableLookup={{
                        localNames: this.props.localNames,
                        projectId: this.props.projectId
                    }}
                    resetValue={OverwriteBehavior.SameAppTypeAndVersion}
                    value={properties["Octopus.Action.ServiceFabric.OverwriteBehavior"]}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.ServiceFabric.OverwriteBehavior"]: x})}
                    label="Overwrite behavior">
                    <RadioButton value={OverwriteBehavior.Never} label="Never"/>
                    <RadioButton value={OverwriteBehavior.Always} label="Always"/>
                    <RadioButton value={OverwriteBehavior.SameAppTypeAndVersion} label="Same app type and version" isDefault/>
                </BoundStringRadioButtonGroup>
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Octopus.Action.ServiceFabric.SkipPackageValidation"
                title="Skip Package Validation"
                summary={properties["Octopus.Action.ServiceFabric.SkipPackageValidation"]
                && properties["Octopus.Action.ServiceFabric.SkipPackageValidation"] !== "False"
                    ? Summary.summary(<span>Tell Service Fabric <strong>not</strong> to perform package validation</span>)
                    : Summary.default("Service Fabric will perform package validation")}
                help="Select to tell Service Fabric not to validate the package before deployment.">
                <BoundStringCheckbox
                    variableLookup={{
                        localNames: this.props.localNames,
                        projectId: this.props.projectId
                    }}
                    resetValue={"False"}
                    value={properties["Octopus.Action.ServiceFabric.SkipPackageValidation"]}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.ServiceFabric.SkipPackageValidation"]: x})}
                    label="Skip validation" />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Octopus.Action.ServiceFabric.CopyPackageTimeoutSec"
                title="Copy Package Timeout"
                summary={properties["Octopus.Action.ServiceFabric.CopyPackageTimeoutSec"]
                    ? Summary.summary(<span>{properties["Octopus.Action.ServiceFabric.CopyPackageTimeoutSec"]} seconds</span>)
                    : Summary.default("Unchanged")}
                help={"Enter a value in seconds to override the timeout for copying an application package to the image store."}>
                <VariableLookupText
                    localNames={this.props.localNames}
                    projectId={this.props.projectId}
                    value={properties["Octopus.Action.ServiceFabric.CopyPackageTimeoutSec"]}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.ServiceFabric.CopyPackageTimeoutSec"]: x})}
                    error={this.props.getFieldError("Octopus.Action.ServiceFabric.CopyPackageTimeoutSec")}
                    label="Copy package timeout (seconds)" />
            </ExpandableFormSection>
        </div>;
    }

    private upgradeBehaviorSummary(): SummaryNode {
        const behavior = this.props.properties["Octopus.Action.ServiceFabric.OverrideUpgradeBehavior"];
        if (behavior === UpgradeBehavior.None) {
            return Summary.default("None");
        }
        if (behavior === UpgradeBehavior.ForceUpgrade) {
            return Summary.summary("Force upgrade");
        }
        if (behavior === UpgradeBehavior.VetoUpgrade) {
            return Summary.summary("Veto upgrade");
        }
        return Summary.summary("Bound to a project variable");
    }

    private overwriteBehaviorSummary(): SummaryNode {
        const behavior = this.props.properties["Octopus.Action.ServiceFabric.OverwriteBehavior"];
        if (behavior === OverwriteBehavior.Never) {
            return Summary.summary("Never");
        }
        if (behavior === OverwriteBehavior.Always) {
            return Summary.summary("Always");
        }
        if (behavior === OverwriteBehavior.SameAppTypeAndVersion) {
            return Summary.default("Same app type and version");
        }
        return Summary.summary("Bound to a project variable");
    }
}

pluginRegistry.registerDeploymentAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    actionType: "Octopus.AzureServiceFabricApp",
    summary: (properties, targetRolesAsCSV) => <AzureServiceFabricAppActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV} />,
    edit: AzureServiceFabricAppActionEdit,
    canHaveChildren: (step) => true,
    canBeChild: true,
    targetRoleOption: TargetRoles.Optional,
    hasPackages: (action) => true,
    features: {
        optional: [
            "Octopus.Features.ConfigurationTransforms",
            "Octopus.Features.ConfigurationVariables",
            "Octopus.Features.CustomScripts",
            "Octopus.Features.JsonConfigurationVariables",
            "Octopus.Features.SubstituteInFiles"
        ]
    }
});


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Actions/azureServiceFabricApp/azureServiceFabricAppAction.tsx